import {
  Creator, Approval, Partnership, Contract, ActivityEvent, Agent, Followup, AppNotification, TimelineEntry,
  Automation, MemoryEntry,
} from '@/types/entities';
import { CONNECTOR_REGISTRY } from '@/services/connectors/registry';
import { isBackendConnected } from '@/config/env';
import { DATA_MODE } from '@/config/dataMode';
import { demoDataService, DemoSettings } from '@/services/demoDataService';
import { liveDataService } from '@/services/liveDataService';

/**
 * DATA SERVICE — the ONLY place a component is allowed to get or mutate
 * entity data. In DATA_MODE='demo' (today, always) every call proxies to
 * demoDataService, which persists to localStorage. The day a real backend
 * exists, each function body below becomes a `fetch('/api/...')` call
 * instead — components never change, because they only ever call through
 * this layer. This is the "Frontend -> Service Layer -> Connector ->
 * External Platform" rule made concrete, now with real local mutations
 * instead of only mock reads.
 */
const delay = <T,>(value: T, ms = 40): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), ms));

/**
 * V8: DATA_MODE can now legitimately be 'live'. This is no longer a hard
 * gate — it exists so every demo-backed function below stays self-
 * documenting about which mode it serves. Modules not yet wired to the
 * V8 backend (Partnerships beyond list/create, Contracts, AI Agents
 * catalog, Connectors, Settings) intentionally keep calling
 * demoDataService even when DATA_MODE === 'live' — seeing local preview
 * data there is honest and expected until those modules get their own
 * live wiring in a later phase.
 */
function assertDemoMode() {
  // no-op — kept as a call-site marker for "this function is demo-only
  // today"; see the comment above.
}

export const dataService = {
  dataMode: DATA_MODE,
  backendConnected: isBackendConnected,

  // ---- Creators --------------------------------------------------
  // Live-wired in V8: list/create/update/archive proxy to the real API.
  // getCreator/timeline notes/hard-delete stay demo-only this pass (the
  // backend has no single-creator-with-timeline or delete-demo route yet
  // beyond what's listed in backend/src/routes/creators.ts).
  async listCreators(): Promise<Creator[]> {
    if (DATA_MODE === 'live') return liveDataService.listCreators();
    return delay(demoDataService.listCreators());
  },
  async getCreator(id: string): Promise<Creator | undefined> { assertDemoMode(); return delay(demoDataService.getCreator(id)); },
  async createCreator(input: Partial<Creator> & { fullName: string; tiktokUsername: string }): Promise<Creator> {
    if (DATA_MODE === 'live') return liveDataService.createCreator(input);
    return delay(demoDataService.createCreator(input), 60);
  },
  async createCreatorLocal(input: { fullName: string; tiktokUsername: string }): Promise<Creator> { return this.createCreator(input); }, // kept for older callers
  async updateCreator(id: string, patch: Partial<Creator>): Promise<Creator | undefined> {
    if (DATA_MODE === 'live') return liveDataService.updateCreator(id, patch);
    return delay(demoDataService.updateCreator(id, patch));
  },
  async addCreatorTimelineEntry(id: string, label: string, note?: string): Promise<TimelineEntry | undefined> { assertDemoMode(); return delay(demoDataService.addCreatorTimelineEntry(id, label, note)); },
  async archiveCreator(id: string): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.archiveCreator(id);
    demoDataService.archiveCreator(id); return delay(undefined);
  },
  async deleteCreator(id: string): Promise<void> { assertDemoMode(); demoDataService.deleteCreator(id); return delay(undefined); },

  // ---- Follow-ups --------------------------------------------------
  async listFollowups(): Promise<Followup[]> { assertDemoMode(); return delay(demoDataService.listFollowups()); },
  async createFollowup(input: Omit<Followup, 'id' | 'createdAt' | 'status'>): Promise<Followup> { assertDemoMode(); return delay(demoDataService.createFollowup(input), 60); },
  async updateFollowup(id: string, patch: Partial<Followup>): Promise<Followup | undefined> { assertDemoMode(); return delay(demoDataService.updateFollowup(id, patch)); },
  async completeFollowup(id: string): Promise<void> { assertDemoMode(); demoDataService.completeFollowup(id); return delay(undefined); },
  async reopenFollowup(id: string): Promise<void> { assertDemoMode(); demoDataService.reopenFollowup(id); return delay(undefined); },
  async deleteFollowup(id: string): Promise<void> { assertDemoMode(); demoDataService.deleteFollowup(id); return delay(undefined); },

  // ---- Approvals --------------------------------------------------
  // Live-wired: list + decide (approve/reject). Archive/delete-demo stay
  // demo-only — the backend deliberately has no delete route for
  // approvals, since brief #10/#7 treat the approval trail as durable.
  async listApprovals(): Promise<Approval[]> {
    if (DATA_MODE === 'live') return liveDataService.listApprovals();
    return delay(demoDataService.listApprovals());
  },
  async decideApproval(id: string, decision: 'approved' | 'rejected'): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.decideApproval(id, decision);
    demoDataService.decideApproval(id, decision); return delay(undefined);
  },
  async archiveApproval(id: string): Promise<void> { assertDemoMode(); demoDataService.archiveApproval(id); return delay(undefined); },
  async deleteApproval(id: string): Promise<void> { assertDemoMode(); demoDataService.deleteApproval(id); return delay(undefined); },

  // ---- Partnerships --------------------------------------------------
  // Live-wired in this pass: list/get/create/update/archive plus the
  // JSONB-backed contacts/timeline/ledger sub-resources (migration 0004).
  // Hard delete stays demo-only — the backend intentionally has no delete
  // route (archive-only, matching Creators/Contracts).
  async listPartnerships(): Promise<Partnership[]> {
    if (DATA_MODE === 'live') return liveDataService.listPartnerships();
    return delay(demoDataService.listPartnerships());
  },
  async getPartnership(id: string): Promise<Partnership | undefined> {
    if (DATA_MODE === 'live') return liveDataService.getPartnership(id);
    return delay(demoDataService.getPartnership(id));
  },
  async createPartnership(input: Partial<Partnership> & { partnerName: string }): Promise<Partnership> {
    if (DATA_MODE === 'live') return liveDataService.createPartnership(input);
    return delay(demoDataService.createPartnership(input), 60);
  },
  async updatePartnership(id: string, patch: Partial<Partnership>): Promise<Partnership | undefined> {
    if (DATA_MODE === 'live') return liveDataService.updatePartnership(id, patch);
    return delay(demoDataService.updatePartnership(id, patch));
  },
  async addPartnershipContact(id: string, contact: Omit<Partnership['contacts'][number], 'id'>) {
    if (DATA_MODE === 'live') return liveDataService.addPartnershipContact(id, contact);
    return delay(demoDataService.addPartnershipContact(id, contact));
  },
  async addPartnershipTimelineEntry(id: string, label: string, note?: string) {
    if (DATA_MODE === 'live') return liveDataService.addPartnershipTimelineEntry(id, label, note);
    return delay(demoDataService.addPartnershipTimelineEntry(id, label, note));
  },
  async addPartnershipLedgerEntry(id: string, entry: Omit<Partnership['ledger'][number], 'id' | 'createdAt'>) {
    if (DATA_MODE === 'live') return liveDataService.addPartnershipLedgerEntry(id, entry);
    return delay(demoDataService.addPartnershipLedgerEntry(id, entry));
  },
  async archivePartnership(id: string): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.archivePartnership(id);
    demoDataService.archivePartnership(id); return delay(undefined);
  },
  async deletePartnership(id: string): Promise<void> { assertDemoMode(); demoDataService.deletePartnership(id); return delay(undefined); },

  // ---- Contracts --------------------------------------------------
  // Live-wired: list/create/update/archive/duplicate-version. Hard delete
  // and single-contract-get stay demo-only (no GET /:id route in V8 yet —
  // the Contracts list page doesn't need it; add if a detail page needs it).
  async listContracts(): Promise<Contract[]> {
    if (DATA_MODE === 'live') return liveDataService.listContracts();
    return delay(demoDataService.listContracts());
  },
  async getContract(id: string): Promise<Contract | undefined> { assertDemoMode(); return delay(demoDataService.getContract(id)); },
  async createContract(input: Partial<Contract> & { partnershipId: string; title: string }): Promise<Contract> {
    if (DATA_MODE === 'live') return liveDataService.createContract(input);
    return delay(demoDataService.createContract(input), 60);
  },
  async updateContract(id: string, patch: Partial<Contract>): Promise<Contract | undefined> {
    if (DATA_MODE === 'live') return liveDataService.updateContract(id, patch);
    return delay(demoDataService.updateContract(id, patch));
  },
  async duplicateContractVersion(id: string): Promise<Contract | undefined> {
    if (DATA_MODE === 'live') return liveDataService.duplicateContractVersion(id);
    return delay(demoDataService.duplicateContractVersion(id), 60);
  },
  async archiveContract(id: string): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.archiveContract(id);
    demoDataService.archiveContract(id); return delay(undefined);
  },
  async deleteContract(id: string): Promise<void> { assertDemoMode(); demoDataService.deleteContract(id); return delay(undefined); },

  // ---- AI Agents --------------------------------------------------
  // Live-wired: full CRUD against the real ai_agents table. Creating or
  // enabling a non-built-in agent here still does NOT grant it autonomous
  // execution — see agents.ts's comment: only creator_manager has real
  // sweep logic wired to it in V8.
  async listAgents(): Promise<Agent[]> {
    if (DATA_MODE === 'live') return liveDataService.listAgents();
    return delay(demoDataService.listAgents());
  },
  async createAgent(input: Partial<Agent> & { name: string; provider: string; model: string }): Promise<Agent> {
    if (DATA_MODE === 'live') return liveDataService.createAgent(input);
    return delay(demoDataService.createAgent(input), 60);
  },
  async updateAgent(id: string, patch: Partial<Agent>): Promise<Agent | undefined> {
    if (DATA_MODE === 'live') return liveDataService.updateAgent(id, patch);
    return delay(demoDataService.updateAgent(id, patch));
  },
  async toggleAgentPause(id: string): Promise<void> {
    if (DATA_MODE === 'live') { await liveDataService.toggleAgentPause(id); return; }
    demoDataService.toggleAgentPause(id); return delay(undefined);
  },
  async archiveAgent(id: string): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.archiveAgent(id);
    demoDataService.archiveAgent(id); return delay(undefined);
  },
  async deleteAgent(id: string): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.deleteAgent(id);
    demoDataService.deleteAgent(id); return delay(undefined);
  },

  // ---- Automations --------------------------------------------------
  // Demo-only today — the V8 backend has no automations table yet, so
  // there is no live branch here (unlike Agents/Creators/etc.), matching
  // the honest "Draft/Waiting Connector, never Active" rule: a real
  // execution backend is a precondition for a live branch to mean anything.
  async listAutomations(): Promise<Automation[]> { assertDemoMode(); return delay(demoDataService.listAutomations()); },
  async getAutomation(id: string): Promise<Automation | undefined> { assertDemoMode(); return delay(demoDataService.getAutomation(id)); },
  async createAutomation(input: Partial<Automation> & { name: string; trigger: string; actions: string }): Promise<Automation> { assertDemoMode(); return delay(demoDataService.createAutomation(input), 60); },
  async updateAutomation(id: string, patch: Partial<Automation>): Promise<Automation | undefined> { assertDemoMode(); return delay(demoDataService.updateAutomation(id, patch)); },
  async duplicateAutomation(id: string): Promise<Automation | undefined> { assertDemoMode(); return delay(demoDataService.duplicateAutomation(id)); },
  async toggleAutomationPause(id: string): Promise<void> { assertDemoMode(); demoDataService.toggleAutomationPause(id); return delay(undefined); },
  async archiveAutomation(id: string): Promise<void> { assertDemoMode(); demoDataService.archiveAutomation(id); return delay(undefined); },
  async deleteAutomation(id: string): Promise<void> { assertDemoMode(); demoDataService.deleteAutomation(id); return delay(undefined); },

  // ---- Shared Memory ("Ugarits Brain") -------------------------------
  // Demo-only today — see types/orchestrator.ts for the future
  // PostgreSQL/pgvector shape this is deliberately not faking yet.
  async listMemory(): Promise<MemoryEntry[]> { assertDemoMode(); return delay(demoDataService.listMemory()); },
  async searchMemory(query: string, domain?: string): Promise<MemoryEntry[]> { assertDemoMode(); return delay(demoDataService.searchMemory(query, domain)); },
  async getMemory(id: string): Promise<MemoryEntry | undefined> { assertDemoMode(); return delay(demoDataService.getMemory(id)); },
  async createMemory(input: Partial<MemoryEntry> & { title: string; domain: string; content: string }): Promise<MemoryEntry> { assertDemoMode(); return delay(demoDataService.createMemory(input), 60); },
  async updateMemory(id: string, patch: Partial<MemoryEntry>): Promise<MemoryEntry | undefined> { assertDemoMode(); return delay(demoDataService.updateMemory(id, patch)); },
  async archiveMemory(id: string): Promise<void> { assertDemoMode(); demoDataService.archiveMemory(id); return delay(undefined); },
  async deleteMemory(id: string): Promise<void> { assertDemoMode(); demoDataService.deleteMemory(id); return delay(undefined); },

  // ---- Activity --------------------------------------------------
  async listActivity(): Promise<ActivityEvent[]> {
    if (DATA_MODE === 'live') return liveDataService.listActivity();
    return delay(demoDataService.listActivity());
  },
  // Export logging stays a local/demo activity note in both modes — it's
  // a UI convenience event, not a real backend audit action.
  async logExport(moduleName: string): Promise<void> { demoDataService.logExport(moduleName); return delay(undefined); },
  async logDiagnosticsRun(issueCount: number): Promise<void> { demoDataService.logDiagnosticsRun(issueCount); return delay(undefined); },
  async logLocalCommand(query: string, matched: boolean): Promise<void> { demoDataService.logLocalCommand(query, matched); return delay(undefined); },

  // ---- Notifications --------------------------------------------------
  async listNotifications(): Promise<AppNotification[]> {
    if (DATA_MODE === 'live') return liveDataService.listNotifications();
    return delay(demoDataService.listNotifications());
  },
  async markNotificationRead(id: string, read = true): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.markNotificationRead(id);
    demoDataService.markNotificationRead(id, read); return delay(undefined);
  },
  async markAllNotificationsRead(): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.markAllNotificationsRead();
    demoDataService.markAllNotificationsRead(); return delay(undefined);
  },
  async clearNotification(id: string): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.clearNotification(id);
    demoDataService.clearNotification(id); return delay(undefined);
  },

  // ---- Settings --------------------------------------------------
  // Live-wired against the singleton app_settings row (migration 0004).
  // PATCH is gated server-side to settings.manage (Owner/Admin) — a
  // lower-permission user's PATCH attempt is rejected by the API with a
  // 403, not silently hidden client-side only.
  async getSettings(): Promise<DemoSettings> {
    if (DATA_MODE === 'live') return liveDataService.getSettings();
    return delay(demoDataService.getSettings());
  },
  async updateSettings(patch: Partial<DemoSettings>): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.updateSettings(patch);
    demoDataService.updateSettings(patch); return delay(undefined);
  },

  // ---- Reset / Clear --------------------------------------------------
  async resetDemoData(): Promise<void> { assertDemoMode(); demoDataService.resetDemoData(); return delay(undefined, 150); },
  async clearAllDemoData(): Promise<void> { assertDemoMode(); demoDataService.clearAllDemoData(); return delay(undefined, 150); },

  // ---- Connectors / System --------------
  // Live-wired: reads/writes the real `connectors` table (seeded
  // not_configured in migration 0002). The V8 backend never fakes a
  // connected connector — status only ever reflects what's actually been
  // configured server-side (brief #10's "never fake successful
  // integrations").
  async listConnectorStatuses(): Promise<{ key: string; displayName: string; category: string; status: string; label?: string; enabledUi: boolean; notes?: string }[]> {
    if (DATA_MODE === 'live') return liveDataService.listConnectorStatuses();
    const results = await Promise.all(
      CONNECTOR_REGISTRY.map(async (c) => {
        const override = demoDataService.getConnectorOverride(c.key);
        return {
          key: c.key,
          displayName: c.displayName,
          category: c.category,
          status: (await c.getStatus()).status,
          label: override.label,
          enabledUi: override.enabledUi ?? true,
          notes: override.notes,
        };
      })
    );
    return delay(results, 60);
  },
  async updateConnectorOverride(key: string, patch: { label?: string; enabledUi?: boolean; notes?: string }): Promise<void> {
    if (DATA_MODE === 'live') return liveDataService.updateConnectorOverride(key, patch);
    demoDataService.setConnectorOverride(key, patch);
    return delay(undefined);
  },
  async listSystemServices() {
    if (DATA_MODE === 'live') {
      try {
        const live = await liveDataService.listSystemServices();
        return [{ key: 'frontend', displayName: 'Frontend (this app)', status: 'operational', detail: 'Served over HTTP/HTTPS.' }, ...live];
      } catch {
        // The backend itself is unreachable — report that honestly rather
        // than falling back to a fake "operational" row.
        return [
          { key: 'frontend', displayName: 'Frontend (this app)', status: 'operational', detail: 'Served over HTTP/HTTPS.' },
          { key: 'backend_api', displayName: 'Backend API', status: 'down', detail: 'VITE_API_BASE_URL is set but the backend did not respond.' },
        ];
      }
    }
    return delay([
      { key: 'frontend', displayName: 'Frontend (this app)', status: 'operational', detail: 'Served over HTTP/HTTPS once deployed — see Dockerfile.' },
      { key: 'backend_api', displayName: 'Backend API', status: 'not_connected', detail: 'No API server deployed yet — dataService.ts persists to localStorage via demoDataService.' },
      { key: 'postgres', displayName: 'PostgreSQL', status: 'not_connected', detail: 'db/migrations/ ready to apply once a DB instance exists.' },
      { key: 'n8n', displayName: 'n8n Orchestration', status: 'not_connected', detail: 'Workflow stubs exist in ugarits-os/n8n/, not yet imported to a running instance.' },
    ]);
  },
};
